import { Autocomplete, InputAdornment, Grid, Typography, Divider } from "@mui/material";
import { isEmpty, isEqual } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { convertBase64ToBlob } from "src/utils/files-utils/convert-file";
import {
  Props,
  IAutocompleteQuickFilter,
  AutocompleteQuickFilterOption,
  AutocompleteQuickFilterOptionValue,
  State,
} from "./interfaces";
import { StyledAutocompleteTextfield } from "./styles";
import { DTOModel } from "../../models/base/dto-model";

const PAGE_SIZE = 1000;

function AutocompleteQuickFilter<
  Q extends IAutocompleteQuickFilter,
  V extends AutocompleteQuickFilterOptionValue,
  R extends DTOModel,
  O extends AutocompleteQuickFilterOption<V, R>
>({
  serviceApi,
  quickFilters,
  quickFilter,
  quickFilterOptionValueKey,
  handleChangeQuickFilter,
  labelWith = "name",
  additionalProps,
  propsFilter,
  selectedValue,
  shrinkedLabel,
  customInputStyle,
  fullWidth = false,
  ExtraEndAdornmentChildren,
  extraEndAdornmentChildrenStyle,
  acceptNoSelectedValue = false,
  selectedValueName,
  customLabelStyle,
  customTextfieldStyle,
  notifyParentWithLoading,
  allowAllOption,
  includeLogo,
  translationRootPath,
}: Props<Q, V, R>): JSX.Element {
  const { t } = useTranslation();
  const allOption = {
    name: "All",
    value: { [quickFilterOptionValueKey]: [] },
    item: {} as DTOModel,
  } as unknown as O;

  const [state, setState] = useState<State<O, V, R>>({
    loading: false,
    error: "",
    totalItems: 0,
    pageNumber: 0,
    loadedItems: [],
    loadedOptions: allowAllOption ? [allOption] : [],
    query: "",
    value: null,
    defaultValue: null,
    logoURL: "",
  });

  //#region load options
  const loadOptionsCallback = useCallback(
    async (signal: AbortSignal) => {
      notifyParentWithLoading && notifyParentWithLoading(true);
      try {
        const response = await serviceApi.loadItems(
          {
            ...propsFilter,
          },
          undefined,
          undefined,
          {
            pageNumber: 0,
            pageSize: PAGE_SIZE,
          },
          true,
          60,
          signal
        );
        if (response.error || signal?.aborted || !signal) {
          setState((state) => ({ ...state, error: response.error }));
        } else {
          const options: O[] = response.items.map((item) => {
            return {
              name: item[labelWith as keyof DTOModel] as string,
              value: {
                [quickFilterOptionValueKey]: [item["id"]] as string[],
              } as V,
              item: item as R,
            } as O;
          });
          setState((state) => ({
            ...state,
            loadedItems: response.items as R[],
            loadedOptions: allowAllOption ? [allOption, ...options] : options,
            totalItems: allowAllOption ? response.totalItems + 1 : response.totalItems,
            query: "",
            value: null,
          }));
        }
      } catch (error) {
        if (!signal.aborted) {
          setState((state) => ({
            ...state,
            error: error as string,
          }));
        }
      } finally {
        setState((state) => ({ ...state, loading: false }));
        notifyParentWithLoading && notifyParentWithLoading(false);
      }
    },
    [quickFilterOptionValueKey, labelWith, serviceApi, notifyParentWithLoading, allowAllOption]
  );
  //#endregion

  useEffect(() => {
    // let timer: ReturnType<typeof setTimeout>;
    const abortController = new AbortController();

    //load onInit
    loadOptionsCallback(abortController.signal);

    return () => {
      // clearTimeout(timer);
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    // let timer: ReturnType<typeof setTimeout>;
    const abortController = new AbortController();

    //load onInit
    !isEmpty(propsFilter) && loadOptionsCallback(abortController.signal);

    return () => {
      // clearTimeout(timer);
      abortController.abort();
    };
  }, [propsFilter]);

  useEffect(() => {
    const quickFilterOption = quickFilters[quickFilter];
    const quickFilterOptionValue = quickFilterOption[quickFilterOptionValueKey as keyof Q[keyof Q]];

    if (
      isEmpty(quickFilterOptionValue) &&
      !!state.loadedOptions[0] &&
      state.loadedOptions.length === state.totalItems
      //this means the props filter has no value to be selected
    ) {
      console.log("the props filter has no value to be selected");
      setState((state) => ({
        ...state,
        value: acceptNoSelectedValue ? null : state.loadedOptions[0],
        query: "",
      }));
      //set the first option as selected
      !acceptNoSelectedValue &&
        handleChangeQuickFilter(
          quickFilter,
          state.loadedOptions[0].value,
          state.loadedItems.filter((item) =>
            isEqual([item.id], state.loadedOptions[0].value[quickFilterOptionValueKey as keyof V])
          )[0] as R
        );
    } else if (
      !isEqual(state.value, state.loadedOptions.filter((lo) => isEqual(lo.value, quickFilters[quickFilter]))[0]) &&
      state.loadedOptions.length === state.totalItems
    ) {
      //this means that the quickfilter has a value and the value exists in the options loaded
      console.log(quickFilter, "quickfilter has a value and the value exists in the options loaded");

      setState((state) => ({
        ...state,
        query: "",
        value: state.loadedOptions.filter((lo) => isEqual(lo.value, quickFilters[quickFilter]))[0],
      }));
    } else if (
      state.loadedOptions
        .flatMap((option) => option.value[quickFilterOptionValueKey as keyof V])
        .filter((option) => quickFilterOptionValue.includes(option)).length === 0 &&
      state.loadedOptions[0] &&
      (!selectedValue || selectedValue.length === 0)
    ) {
      handleChangeQuickFilter(
        quickFilter,
        state.loadedOptions[0].value,
        state.loadedItems.filter((item) =>
          isEqual([item.id], state.loadedOptions[0].value[quickFilterOptionValueKey as keyof V])
        )[0] as R
      );
    }
  }, [
    acceptNoSelectedValue,
    selectedValue,
    state.loadedOptions,
    state.totalItems,
    quickFilters,
    quickFilter,
    quickFilterOptionValueKey,
    handleChangeQuickFilter,
    state.loadedItems,
    state.value,
    propsFilter,
  ]);

  useEffect(() => {
    const filteredOptions = state.loadedItems
      .filter(
        (item: any) =>
          item["code"]?.toLowerCase().includes(state.query.toLowerCase()) ||
          item["name"]?.toLowerCase().includes(state.query.toLowerCase()) ||
          item["description"]?.toLowerCase().includes(state.query.toLowerCase()) ||
          item[labelWith]?.toLowerCase().includes(state.query.toLowerCase())
      )
      .map(
        (item: any) =>
          ({
            name: item[labelWith],
            value: { [quickFilterOptionValueKey]: [item["id"]] },
            item,
          } as O)
      );
    setState((state) => ({
      ...state,
      loadedOptions: allowAllOption ? [allOption, ...filteredOptions] : filteredOptions,
    }));
  }, [state.query, labelWith, quickFilterOptionValueKey, state.loadedItems, allowAllOption]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      query: "",
    }));
  }, [state.value]);

  //#region set logo
  useEffect(() => {
    if (includeLogo && state.value?.item[includeLogo.logoKey] && state.value?.item[includeLogo.logoTypeKey]) {
      const logoBase64: string = state.value?.item[includeLogo.logoKey] as string;
      const logoBlob: Blob | undefined = convertBase64ToBlob(logoBase64);
      if (logoBlob) {
        const logoURL: string = URL.createObjectURL(logoBlob);
        setState((state) => ({ ...state, logoURL }));
      }
    } else {
      setState((state) => ({ ...state, logoURL: "" }));
    }
  }, [state.value, includeLogo]);
  //#endregion

  return (
    <Autocomplete
      loading={state.loading}
      options={[...state.loadedOptions]}
      filterOptions={(options) => options}
      value={state.value ?? null}
      renderInput={(params) => (
        <StyledAutocompleteTextfield
          {...params}
          placeholder={selectedValueName ?? t("components.autocomplete_dropdown.placeholders.please_select")}
          InputProps={{
            ...params.InputProps,
            ...(shrinkedLabel && {
              startAdornment: (
                <InputAdornment
                  position={"start"}
                  style={{
                    ...customLabelStyle,
                    display: "flex",
                    height: "100%",
                    gap: "5px",
                  }}
                >
                  <Typography>{shrinkedLabel}</Typography>
                  <Divider
                    orientation={"vertical"}
                    flexItem
                  />
                </InputAdornment>
              ),
            }),
            endAdornment: (
              <>
                {includeLogo && state.logoURL && (
                  <img
                    width="35"
                    src={state.logoURL}
                    alt={"logo"}
                  />
                )}
                {params.InputProps.endAdornment}
                {ExtraEndAdornmentChildren && (
                  <InputAdornment position="end">
                    <Grid>
                      <ExtraEndAdornmentChildren
                        customStyle={extraEndAdornmentChildrenStyle}
                        ids={state.value?.value}
                        optionName={state.value?.name}
                      />
                    </Grid>
                  </InputAdornment>
                )}
              </>
            ),
            style: { ...customInputStyle, paddingRight: "50px" },
          }}
          sx={customTextfieldStyle}
          // style={{flexWrap: "nowrap"}}
        />
      )}
      // fullWidth={fullWidth}
      onInputChange={(_, value) => {
        setState((state) => ({
          ...state,
          query: value,
        }));
      }}
      ListboxProps={{
        style: { maxHeight: "150px", background: "white" },
      }}
      onChange={(_, value, reason) => {
        setState((state) => ({
          ...state,
          query: "",
        }));
        reason === "clear" && acceptNoSelectedValue
          ? handleChangeQuickFilter(quickFilter, { [quickFilterOptionValueKey]: [] as string[] } as V, null)
          : !!value?.value &&
            handleChangeQuickFilter(
              quickFilter,
              value.value,
              state.loadedItems.filter((item) =>
                isEqual([item.id], value.value[quickFilterOptionValueKey as keyof V])
              )[0] as R
            );
      }}
      getOptionLabel={(option) => {
        const name =
          additionalProps && option.item[additionalProps]
            ? `${option.item[additionalProps]} (${option.name})`
            : option.name;
        return translationRootPath ? `${t(`${translationRootPath}.${name}`)}` : name;
      }}
      isOptionEqualToValue={(option, value) => isEqual(option.value, value?.value)}
    />
  );
}

export default AutocompleteQuickFilter;
